/**
 * The `/api/log` ingest boundary's published limits on `LogError`.
 *
 * The ingest route is unauthenticated, so its Zod schema *is* the trust
 * boundary and stays strict: an entry that violates it is dropped whole and
 * the caller still gets a 2xx (see ingest.ts — drop what does not parse,
 * forward the rest, always answer 2xx). Nothing on the server may repair an
 * over-long value, because repairing it means mutating untrusted input to
 * make it pass the very check it just failed.
 *
 * That leaves the client as the only correct place to keep a real event
 * from turning into a silent drop, and the client needs the same numbers
 * the schema enforces. They live here, in the environment-agnostic package
 * both sides can import (a Worker route may depend on `@bindery/logging`;
 * the reverse is not true), rather than as literals duplicated across the
 * schema and the clamp. `Logger.normalizeError` clamps to these; the
 * route's schema enforces them; neither owns a copy.
 *
 * Denominated in UTF-16 code units — JS `.length` — because that is what
 * `z.string().max()` counts, so a `.length` clamp on the client lands
 * exactly on the boundary rather than near it. Verified against the
 * zod 4.4.3 this repo resolves: `z.string().max(4)` accepts `"中".repeat(4)`
 * (`.length` 4, 12 UTF-8 bytes) and rejects `"abcde"`.
 *
 * The route's *other* caps — the `data` bag and the whole POST body — are
 * byte-denominated and deliberately not published here. A client cannot
 * clamp a structured bag without deciding which of a call site's keys to
 * sacrifice, so those stay local to ingest.ts as pure boundary checks.
 */

/**
 * `domain.action` shape (policy §2): at least one dot, lowercase
 * segments/underscores only. Deliberately not pinned to a runtime domain
 * list, so adding a domain does not require a matching ingest change.
 */
export const EVENT_NAME_PATTERN = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/;

/** Cap on `LogError.type` (the error's `name`). */
export const MAX_ERROR_TYPE_LENGTH = 256;

/**
 * Cap on `LogError.message`. The value most likely to blow a cap:
 * `new Error("request failed: " + (await res.text()))` against an HTML
 * error page clears 4096 characters without trying.
 */
export const MAX_ERROR_MESSAGE_LENGTH = 4096;

/** Cap on `LogError.code`. */
export const MAX_ERROR_CODE_LENGTH = 64;
